<template>
  <div class="variable-selector-container" ref="containerRef">     
    <!-- 选择框头部 -->
    <div class="selector-header" @click="toggleDropdown" :class="{ 'open': dropdownVisible }" >
      <div v-if="selectedVariables.length > 0" class="selected-tags">
        <el-tag
          v-if="selectedVariables[0]"
          closable
          @close="removeSelectedVariable(0)"
          class="variable-tag"
        >
          {{ selectedVariables[0].display_name }}
        </el-tag>
          <!-- 如果还有更多选中的变量，显示 +N -->
        <el-tag
          v-if="selectedVariables.length > 1"
          class="variable-tag count-tag"
        >
          +{{ selectedVariables.length - 1 }}
        </el-tag>
      </div>
      <span v-else class="placeholder">{{ t('message.varableSelectorControl.pleaseSelect') }}</span>
            <div 
        class="dropdown-icon" 
        @mouseenter="isHovering = true"
        @mouseleave="isHovering = false"
        @click.stop="handleIconClick"
      >
        <el-icon :class="{ 'reverse': dropdownVisible }">
          <component :is="getIconComponent" />
        </el-icon>
      </div>      
    </div>
    
    <!-- 下拉面板内容 -->
    <div 
      v-show="dropdownVisible" 
      class="selector-dropdown" 
      :class="variableSelectorPopper"
      @click.stop
    >
      <div class="selector-panel">
        <!-- 左侧从机列表 -->
        <div class="slave-panel">
          <div class="panel-header">{{ t('message.varableSelectorControl.slaveList') }}</div>
          <el-input
            v-model="slaveSearch"
            :placeholder= "$t('message.varableSelectorControl.inputSlaveName')"
            size="small"
            clearable
            class="search-input"
          />
          <el-scrollbar class="slave-list">
            <div
              v-for="slave in filteredSlaves"
              :key="slave.slave_id"
              class="slave-item"
              :class="{ active: activeSlave === slave.slave_id }"
              @click="selectSlave(slave.slave_id)"
            >
              {{ slave.slave_name }}
            </div>
          </el-scrollbar>
        </div>

        <!-- 右侧变量列表 -->
        <div class="variable-panel">
          <div class="panel-header">{{ t('message.varableSelectorControl.variableList') }}</div>
            <div class="search-group-row">
            <el-select
              v-model="selectedGroupIds"
              :placeholder="$t('message.realTimeData.selectVariableGroup')"
              size="small"
              multiple
              collapse-tags
              collapse-tags-tooltip
              clearable
              class="group-select"
              @change="handleGroupChange"
            >
              <el-option
                v-for="group in VarGroupOptions"
                :key="group.value"
                :label="group.label"
                :value="group.value"
              />
            </el-select>

          <el-input
            v-model="variableSearch"
            :placeholder= "$t('message.varableSelectorControl.searchVariable')"
            size="small"
            clearable 
            class="search-input"            
          >
          <template #append>
              <el-button                 
                :icon="Search"
                size="small" 
                @click="handleSearch"
                class="search-button"
              />
            </template>
          </el-input>
          </div>
          <el-scrollbar class="variable-list">
            <div class="variable-checkbox-group">
              <div
                v-for="variable in filteredVariables"
                :key="variable.id"
                class="variable-item"
              >
                <label class="custom-checkbox">
                  <input 
                    type="checkbox" 
                    :checked="isVariableSelected(variable.name)"
                    @change="toggleVariableSelection(variable)"                    
                  >
                  <span>{{ variable.display_name }}</span>
                </label>
              </div>
            </div>
          </el-scrollbar>
          
          <!-- 分页控件 -->
          <div class="pagination">
             <el-pagination
              small
              layout="prev, pager, next"
              :total="pagination.total"
              :page-size="pagination.pageSize"
              :current-page="pagination.currentPage"
              @current-change="handlePageChange"
            />
            <span class="total-count">{{ t('message.varableSelectorControl.totalCount', { total: pagination.total }) }}</span>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive,computed, onMounted,watch,onUnmounted,defineProps,defineEmits,nextTick} from 'vue';
import { useEventListener } from '@vueuse/core'; // 需要安装 vueuse
import { vardetailbydevice,slavedetailbydevice, vargroupdetailbydevice } from '@/api/TemplateManagement'
import { Search,ArrowDown,Close} from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus';
import { useI18n } from 'vue-i18n'
//import { useResizeObserver } from '@vueuse/core'; // 改用 vueuse 的安全监听

const props = defineProps({
  deviceId: {
    type: Number,
    default: 4
  },
  showAllSlave: {
    type: Boolean,
    default: true
  },
  modelValue: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const { t } = useI18n()
const slaveOptions = ref([]) // 定义 slaveOptions
const containerRef = ref(null);
const dropdownVisible = ref(false);
const slaveSearch = ref('');
const variableSearch = ref('');
const activeSlave = ref(1); // 默认选中第一个从机
const variableOptions = ref([]);

// 新增：变量分组相关
const VarGroupOptions = ref([])
const selectedGroupIds = ref([])
const selectedGroupNames = ref([])

const selectedDeviceId = ref(props.deviceId)
// 修改：selectedVariables 现在存储对象数组，包含 name 和 display_name
const selectedVariables = ref([...props.modelValue])
const isHovering = ref(false); // 新增：鼠标悬停状态

 const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0
})



// 修改加载分组函数，确保包含"全部分组"选项
const loadVariableGroups = async (deviceId, slaveId) => {
  try {
    if (!deviceId) return;
    
    const res = await vargroupdetailbydevice({
      deviceId: deviceId,
      slaveId: slaveId || undefined,
      pageNum: 1,
      pageSize: 1000
    });
    
    if (res.success) {
      VarGroupOptions.value = [
        { value: -1, label: t('message.realTimeData.allGroups') },
        ...res.data.list.map(group => ({
          value: group.id,
          label: group.name_group
        }))
      ];

      // 默认选中"全部分组"
      if (VarGroupOptions.value.length > 0) {
        selectedGroupIds.value = [-1];
        selectedGroupNames.value = [t('message.realTimeData.allGroups')];
      }
    }    
  } catch (error) {
    console.error('加载变量分组失败:', error);
  }
};


// 修改处理函数
const handleGroupChange = async (groupIds) => {
  try {
    // 获取选中的分组名称
    selectedGroupNames.value = groupIds.map(groupId => {
      const selectedOption = VarGroupOptions.value.find(option => option.value === groupId);
      return selectedOption ? selectedOption.label : '';
    }).filter(name => name);

    console.log('选中的分组IDs:', groupIds);
    console.log('选中的分组名称:', selectedGroupNames.value);
    
    //加载搜索数据
    await handleSearch();
    
  } catch (error) {
    ElMessage.error(t('message.varableSelectorControl.loadGroupFailed'));
    console.error('处理分组变更失败:', error);
  }
};

// 新增：计算图标组件
const getIconComponent = computed(() => {
  // 如果有选中变量且鼠标悬停，显示删除图标
  if (selectedVariables.value.length > 0 && isHovering.value) {
    return Close;
  }
  // 否则显示下拉箭头
  return ArrowDown;
});

// 新增：图标点击处理
const handleIconClick = () => {
  if (selectedVariables.value.length > 0 && isHovering.value) {
    // 如果鼠标在图标上且有选中变量，执行清空操作
    clearAllSelectedVariables();
  } else {
    // 否则切换下拉框
    toggleDropdown();
  }
};

// 新增：清空所有选中的变量
const clearAllSelectedVariables = () => {
  if (selectedVariables.value.length === 0) return;
    selectedVariables.value = [];
    emit('update:modelValue', []);
    emit('change', []);    


  /* // 确认对话框（可选）
  if (confirm(t('message.varableSelectorControl.confirmClearAll'))) {
    selectedVariables.value = [];
    emit('update:modelValue', []);
    emit('change', []);    
  } */
};

// 检查变量是否已选中
const isVariableSelected = (variableName) => {
  return selectedVariables.value.some(v => v.name === variableName);
};

// 添加移除选定变量的方法
const removeSelectedVariable = (index) => {

   try {
    // 1. 验证输入参数
    if (typeof index !== 'number' || index < 0 || index >= selectedVariables.value.length) {
      throw new Error(t('message.varableSelectorControl.invalidIndex'));
    }

    // 2. 获取要移除的变量名（确保是字符串类型）    
    const variable = selectedVariables.value[index];
    if (variable && variable.name) {
      toggleVariableSelection(variable);
    }
  
  } catch (error) {    
    ElMessage.warning(error.message);
  }  
};

const handleSearch = async() => {
  pagination.currentPage = 1; // 重置到第一页  
  // 如果需要根据从机ID和搜索词加载变量
  if (activeSlave.value) {
    loadVariables(activeSlave.value,variableSearch.value);
  }  
};


// 修改加载从机方法
const loadSlave = async (deviceId) => {
  try {

    if (!deviceId) {
      ElMessage.warning(t('message.varableSelectorControl.selectDeviceFirst'))
      return
    }
    
    const res = await slavedetailbydevice({
      deviceId: deviceId,
      pageNum: 1,
      pageSize: 1000
    })
    
    if (res.success) {       
        // 先处理接口返回的数据
      const fetchedSlaves = res.data.list || [];
      // 定义“全部从机”的对象，这里的 id 等字段可根据实际需求设置，比如给个特殊标识
      const allSlave = {        
        slave_name:  t('message.varableSelectorControl.allSlaves'),
        slave_id: null // 假设原来的从机对象用 slave_id 标识，这里也保持对应结构，值也可按需设
      };
      // 往从机数据列表开头插入“全部从机”选项
      slaveOptions.value = [allSlave, ...fetchedSlaves];


      // 默认选中第一个从机
      if (slaveOptions.value.length > 1) {
        selectSlave(slaveOptions.value[1].slave_id)        
      }
    }
  } catch (error) {    
    ElMessage.error(t('message.error.loadSlaveFailed'))
  }
}


// 修改加载变量方法
const loadVariables = async (slaveId,name = null) => {
  try {
        
        let nameGroups = undefined;
        if (selectedGroupIds.value.length > 0) {
          // 排除"全部分组"选项(-1)，只传递实际选中的分组
          const validGroupIds = selectedGroupIds.value.filter(id => id !== -1);
          if (validGroupIds.length > 0) {
            // 根据ID获取对应的分组名称
            nameGroups = validGroupIds.map(groupId => {
              const group = VarGroupOptions.value.find(g => g.value === groupId);
              return group ? group.label : null;
            }).filter(name => name);
          }
        }

    const params = {
      pageNum: pagination.currentPage,
      pageSize: pagination.pageSize,
      slaveId: slaveId,
      name: undefined,
      display_name:name,
      name_group:nameGroups && nameGroups.length > 0 ? nameGroups : undefined,
      deviceId: selectedDeviceId.value
    };
    
    const res = await vardetailbydevice(params);
    pagination.total = 0
    if (res.success) {        
      variableOptions.value = res.data.list || [];      
     // ElMessage.error(`${activeSlave.value}=${ variableOptions.value.length} loadVariables`);

      pagination.total = res.data.totalCount

      if(res.data.list.length <= 0) {
        ElMessage.warning(t('message.varableSelectorControl.noVariableData'));
      }
    } else {
      
      ElMessage.warning(res?.msg || t('message.reaTimePlot.loadVariableListFailed'));
    }
  } catch (error) {
    ElMessage.error(t('message.reaTimePlot.loadVariableListFailed'));    
  }
};

// 计算属性：过滤从机列表
const filteredSlaves = computed(() => {
  if(!slaveSearch.value) return slaveOptions.value

  return slaveOptions.value.filter(slave => {    
    return slave.slave_name.toLowerCase().includes(slaveSearch.value.toLowerCase());
  });
});

//计算属性：过滤变量列表
const filteredVariables = computed(() => {
  return   variableOptions.value;   
});
 


// 下拉框样式
const variableSelectorPopper = 'variable-selector-popper';

// 方法
const toggleDropdown = () => {
  dropdownVisible.value = !dropdownVisible.value;
   
  if(dropdownVisible.value)
  {
      pagination.total =0;
     // 如果没有从机数据且已选择设备，则加载从机
      if (selectedDeviceId.value && slaveOptions.value.length === 0) {        
        loadSlave(selectedDeviceId.value)
           
      } 
  }   

};



const selectSlave =(slaveId) => {
  activeSlave.value = slaveId;
  pagination.currentPage = 1;

    // 加载分组信息
  loadVariableGroups(selectedDeviceId.value, slaveId);

  loadVariables(slaveId)
};

// 修改 toggleVariableSelection 方法
// 修改：切换变量选择，现在接收整个变量对象 
const toggleVariableSelection = (variable) => {
  let newSelected = [...selectedVariables.value];
  
  const existingIndex = newSelected.findIndex(v => v.name === variable.name);
  
  if (existingIndex > -1) {
    // 如果已存在，移除
    newSelected.splice(existingIndex, 1);
  } else {
    // 如果不存在，添加（包含 name 和 display_name）
    newSelected.push({
              name: variable.name,
      display_name: variable.display_name,
              mult:variable.mult,
           minimum:variable.minimum,
           maximum:variable.maximum,
       var_decimal:variable.var_decimal
    });
    //ElMessage.error(`-toggleVariableSelection-mult:${variable.mult}-minimum:${variable.minimum}--maximum:${variable.maximum }-var_decimal:${variable.var_decimal }--`);
  }
  
   selectedVariables.value = newSelected;

    // 等待当前渲染周期完成
    nextTick();
 
    emit('update:modelValue', newSelected);
    emit('change', newSelected);
 
        // 传递包含 name 和 display_name 的对象数组
  //emit('update:modelValue', newSelected);
  //emit('change', newSelected); 
      

  
};

const handlePageChange = (page) => {
  pagination.currentPage = page;    
  loadVariables(activeSlave.value)
};

// 点击外部关闭下拉框
useEventListener('click', (e) => {
  if (containerRef.value && !containerRef.value.contains(e.target)) {
    dropdownVisible.value = false;
  }
});

// 在 onMounted 中延迟初始化，避免与 Element Plus 组件竞争
onMounted(async () => {
  // 延迟 100ms 初始化，等待 Element Plus 组件渲染完成
  await new Promise(resolve => setTimeout(resolve, 100));
  
  console.log('初始化数据:', {
    slaveOptions: slaveOptions.value,
    variables: variableOptions.value,
    activeSlave: activeSlave.value
  });

  // 如需监听容器尺寸，使用带防抖的安全监听（可选）
/*   if (containerRef.value) {
    useResizeObserver(containerRef.value, () => {
      // 防抖处理：避免频繁触发
      clearTimeout(window.resizeTimer);
      window.resizeTimer = setTimeout(() => {
        // 业务逻辑（如需要）
      }, 150);
    });
  } */
});

onUnmounted(() => {
  //clearTimeout(window.resizeTimer);
  // 清理事件监听
});

watch(() => props.deviceId, (newVal) => {
  selectedDeviceId.value = newVal  
  slaveOptions.value = []
  variableOptions.value = [];
  selectedVariables.value =[];
  // 重新加载从机
  if (dropdownVisible.value) {    
    loadSlave(newVal)
  }
})


// 修改：确保外部传入的 modelValue 是正确格式
watch(() => props.modelValue, (newVal) => {
  // 确保 newVal 是对象数组格式
  if (Array.isArray(newVal) && newVal.every(item => item && item.name && item.display_name)) {
    selectedVariables.value = [...newVal];
  } else {
    // 如果是旧格式（字符串数组），转换为新格式
    selectedVariables.value = newVal.map(item => {
      if (typeof item === 'string') {
        return { name: item, display_name: item };
      }
      return item;
    });
  }
})
 

</script>

<style scoped>
.variable-selector-container {
  width: 100%;
  position: relative;
  display: inline-block;
}

.selector-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid var(--el-border-color);
  border-radius: 4px;
  cursor: pointer;
  background-color: #fff;
  user-select: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.selector-header.open {
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
}

.selector-header .placeholder {
  color: #909399;
}

.selector-header i {
  margin-left: 8px;
  transition: transform 0.3s;
}

.selector-header.open i {
  transform: rotate(180deg);
}

.selector-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  border: 1px solid var(--el-border-color);
  border-top: none;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 999;
  max-height: 400px;
  overflow: hidden;
}

.variable-selector-popper {
  width: 600px !important;
}

.selector-panel {
  display: flex;
  height: 300px;
}

.slave-panel {
  width: 200px;
  border-right: 1px solid var(--el-border-color);
  display: flex;
  flex-direction: column;
}

.variable-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 8px 12px;
  font-weight: bold;
  background-color: var(--el-fill-color-light);
  border-bottom: 1px solid var(--el-border-color);
}

/* .search-input {
  margin: 8px;
  width: calc(100% - 16px);
} */


/* 新增：搜索和分组行样式 */
.search-group-row {
  display: flex;
  gap: 8px;
  padding: 8px;
  align-items: center;
}

.search-group-row .search-input {
  flex: 1;
  margin: 0;
}

.search-group-row .group-select {
  width: 150px;
  margin: 0;
}

.slave-list, .variable-list {
  flex: 1;
  overflow: hidden;
}

.slave-item {
  padding: 8px 16px;
  cursor: pointer;
}

.slave-item:hover {
  background-color: var(--el-fill-color-light);
}

.slave-item.active {
  background-color: var(--el-color-primary-light-9);
  color: var(--el-color-primary);
}

.variable-item {
  padding: 8px 16px;
}

.variable-checkbox-group {
  flex: 1;
  overflow-y: auto;
}

.custom-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.custom-checkbox input {
  margin-right: 8px;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-top: 1px solid var(--el-border-color);
}

.total-count {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  margin-right: 10px;
}

/* 新增样式 */

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: calc(100% - 24px); 
} 


 .variable-tag {
  margin-right: 4px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
} 


/* 调整选择框头部样式以适应标签 */
.selector-header {
  min-height: 32px;
  align-items: flex-start;
  padding-top: 4px;
  padding-bottom: 4px;
} 

/* 新增样式 */
.dropdown-icon {
  margin-left: auto;
  padding-left: 8px;
  display: flex;
  align-items: center;
  transition: transform 0.3s;
}

.dropdown-icon .el-icon {
  transition: transform 0.3s;
  color: var(--el-text-color-secondary);
}

.dropdown-icon .reverse {
  transform: rotate(180deg);
}

/* 删除图标悬停效果 */
.dropdown-icon:hover .el-icon {
  color: var(--el-color-danger);
}


/* 调整选择框头部样式 */
.selector-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  border: 1px solid var(--el-border-color);
  border-radius: 4px;
  background-color: #fff;
  min-height: 32px;
}

/* 调整标签容器 */
.selected-tags {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  overflow: hidden;
}

.height-40 {
  height: 40px;
}

.height-40 .selector-header {
  height: 100%;
  min-height: 100%;
  line-height: 38px;
}
</style>